import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ChevronUp, ChevronDown, Trash2, X, ImagePlus, Plus, Loader2 } from 'lucide-react';
import { LAYOUTS } from '@/components/admin/GalleryRowsEditor';
import BulkUploadButton from '@/components/admin/BulkUploadButton';
import { uploadFile, uploadFiles } from '@/lib/upload';

const layoutBy = (id) => LAYOUTS.find(l => l.id === id) || LAYOUTS[0];
const layoutFor = (n) => (n >= 4 ? '4-equal' : n === 3 ? '3-equal' : n === 2 ? '2-equal' : 'solo');

// Creates rows from a list of urls, filling rows of 3 (smaller last row).
export async function addRowsFromUrls(scope, urls, startOrder) {
  let order = startOrder;
  for (let i = 0; i < urls.length; i += 3) {
    const chunk = urls.slice(i, i + 3);
    await api.post('/site/gallery-rows', { scope, layout: layoutFor(chunk.length), images: chunk, order: order++ });
  }
}

// In-page photo block editor: drag photos between slots (any row), drop files from your computer, bulk add.
export default function PhotoRowsInline({ scope, rows, reload }) {
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const nextOrder = rows.length ? Math.max(...rows.map(r => r.order ?? 0)) + 1 : 0;

  const patch = (id, body) => api.patch(`/site/gallery-rows/${id}`, body);
  const run = async (fn) => { setBusy(true); try { await fn(); await reload(); } catch { toast.error('Save failed'); } finally { setBusy(false); } };

  const bulkAdd = (urls) => run(() => addRowsFromUrls(scope, urls, nextOrder));
  const dropZoneFiles = async (e) => {
    e.preventDefault(); setOver(false);
    if (!e.dataTransfer.files?.length) return;
    setBusy(true);
    const urls = await uploadFiles(e.dataTransfer.files, 'gallery');
    setBusy(false);
    if (urls.length) bulkAdd(urls);
  };

  const slotImgs = (row, len) => { const a = [...(row.images || [])]; while (a.length < len) a.push(''); return a; };

  const dropOnSlot = (e, row, idx) => {
    e.preventDefault(); e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) return run(async () => { const url = await uploadFile(file, 'gallery'); const a = slotImgs(row, idx + 1); a[idx] = url; await patch(row.id, { images: a }); });
    const raw = e.dataTransfer.getData('application/x-photo');
    if (!raw) return;
    const src = JSON.parse(raw);
    if (src.rowId === row.id && src.idx === idx) return;
    return run(async () => {
      const srcRow = rows.find(r => r.id === src.rowId);
      if (src.rowId === row.id) {
        const a = slotImgs(row, Math.max(idx, src.idx) + 1);
        [a[idx], a[src.idx]] = [a[src.idx], a[idx]];
        await patch(row.id, { images: a });
      } else {
        const a = slotImgs(row, idx + 1), b = slotImgs(srcRow, src.idx + 1);
        [a[idx], b[src.idx]] = [b[src.idx], a[idx]];
        await Promise.all([patch(row.id, { images: a }), patch(srcRow.id, { images: b })]);
      }
    });
  };

  const setSlot = (row, idx, url) => run(async () => { const a = slotImgs(row, idx + 1); a[idx] = url; await patch(row.id, { images: a }); });
  const changeLayout = (row, id) => run(() => { const lo = layoutBy(id); const a = slotImgs(row, lo.slots); return patch(row.id, { layout: id, images: a.slice(0, lo.slots) }); });
  const move = (i, d) => run(() => {
    const ids = rows.map(r => r.id); const j = i + d;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    return Promise.all(ids.map((id, k) => patch(id, { order: k })));
  });
  const remove = (row) => { if (window.confirm('Delete this photo row?')) run(() => api.delete(`/site/gallery-rows/${row.id}`)); };
  const addEmpty = () => run(() => api.post('/site/gallery-rows', { scope, layout: '3-equal', images: ['', '', ''], order: nextOrder }));

  return (
    <div className="space-y-4" data-testid={`photo-rows-inline-${scope}`}>
      <div onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)} onDrop={dropZoneFiles}
        className={`rounded-lg border-2 border-dashed p-4 flex flex-wrap items-center gap-3 ${over ? 'border-gold bg-gold/10' : 'border-royal/30 bg-royal/5'}`}>
        <div className="text-xs text-royal font-semibold flex-1 min-w-[200px]">Photo blocks — drop photos here from your computer, or drag any photo onto another slot to rearrange.</div>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-royal" />}
        <BulkUploadButton onDone={bulkAdd} label="Add photos" testid={`inline-bulk-${scope}`} />
        <button onClick={addEmpty} data-testid={`inline-add-row-${scope}`} className="inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-royal text-white"><Plus className="w-4 h-4" /> Empty row</button>
      </div>
      {rows.map((r, i) => {
        const lo = layoutBy(r.layout);
        const imgs = slotImgs(r, lo.slots);
        return (
          <div key={r.id} className="border border-gold/40 rounded-lg p-3 bg-white/70" data-testid={`inline-row-${r.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <select value={r.layout || '3-equal'} onChange={(e) => changeLayout(r, e.target.value)} className="text-xs border rounded px-2 py-1 text-charcoal" data-testid={`inline-row-layout-${r.id}`}>
                {LAYOUTS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
              <div className="ml-auto flex gap-1 text-charcoal">
                <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => remove(r)} data-testid={`inline-row-delete-${r.id}`} className="p-1 rounded text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className={`grid ${lo.cols} gap-2`}>
              {imgs.slice(0, lo.slots).map((u, idx) => (
                <Slot key={idx} url={u} row={r} idx={idx} onDrop={dropOnSlot} onSet={setSlot} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Slot({ url, row, idx, onDrop, onSet }) {
  const [over, setOver] = useState(false);
  const pick = () => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*';
    inp.onchange = async () => { if (inp.files?.[0]) onSet(row, idx, await uploadFile(inp.files[0], 'gallery')); };
    inp.click();
  };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { setOver(false); onDrop(e, row, idx); }}
      data-testid={`inline-slot-${row.id}-${idx}`}
      className={`relative aspect-[4/3] rounded overflow-hidden group ${over ? 'ring-4 ring-gold' : ''} ${url ? '' : 'border-2 border-dashed border-gold/50 bg-royal/5'}`}>
      {url ? (
        <>
          <img src={url} alt="" draggable onDragStart={(e) => e.dataTransfer.setData('application/x-photo', JSON.stringify({ rowId: row.id, idx }))}
            className="w-full h-full object-cover cursor-grab" />
          <button onClick={() => onSet(row, idx, '')} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5 text-red-600" /></button>
        </>
      ) : (
        <button onClick={pick} className="w-full h-full flex flex-col items-center justify-center text-xs text-charcoal-muted"><ImagePlus className="w-5 h-5 mb-1" />Drop or click</button>
      )}
    </div>
  );
}
